import * as XLSX from 'xlsx';
import { normalizeHeader, aliasSet } from './templateHeaders';
import { specColumnAliases } from './templateSpecs';
import { normalizeDepMethod, DEFAULT_DEP_METHOD } from './depreciationMethods';

const r2 = n => Math.round(n * 100) / 100;

// ── Date parsing (Excel serial | ISO | US | YYYY/MM/DD) ──────
function parseExcelDate(val) {
    if (val == null || val === '') return null;
    if (typeof val === 'number' && isFinite(val)) {
        const unixMs = (val - 25569) * 86400 * 1000;
        const d = new Date(unixMs);
        if (isNaN(d.getTime())) return null;
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(d.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${dd}`;
    }
    const s = String(val).trim();
    if (!s) return null;
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
    if (iso) {
        const [, y, m, d] = iso;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    const us = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/.exec(s);
    if (us) {
        let [, m, d, y] = us;
        if (y.length === 2) y = (parseInt(y, 10) > 50 ? '19' : '20') + y;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    const alt = /^(\d{4})\/(\d{1,2})\/(\d{1,2})/.exec(s);
    if (alt) {
        const [, y, m, d] = alt;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return null;
}

function parseNumber(val) {
    if (val == null || val === '') return 0;
    if (typeof val === 'number') return r2(val);
    const s = String(val).replace(/[$,()%]/g, '').trim();
    if (!s || s === '-') return 0;
    const isNeg = String(val).includes('(') && String(val).includes(')');
    const n = parseFloat(s);
    if (isNaN(n)) return 0;
    return r2(isNeg ? -n : n);
}

function parseIntSafe(val, fallback = 0) {
    if (val == null || val === '') return fallback;
    if (typeof val === 'number' && isFinite(val)) return Math.round(val);
    const s = String(val).replace(/[^0-9.-]/g, '').trim();
    if (!s) return fallback;
    const n = parseInt(s, 10);
    return isNaN(n) ? fallback : n;
}

/* W-063 (Brian 09-29): the LEGACY position-based D&A-schedule reader (parseLegacy, with detectSection / inferAssetType /
 * FA_TYPES / IA_TYPES) is RETIRED. A sheet with no header row naming description + bs_line now BLOCKS at upload (B7), so
 * its rows can never be imported -- and its only consumer, Step 8's keyword guess of the balance-sheet line
 * (matchBsAssetLine), is deleted with it. An asset's line is always ASKED (the bs_line column), never guessed. */

// ── AST-1: canonical header-named layout ─────────────────────
// The canonical asset template is a flat clean list with a header row:
//   description | type | cost | in_service_date | disposal_date | useful_life | bs_line | salvage_value | method
// where `type` is FA/IA (the section) and `bs_line` is the HUMAN-READABLE Balance Sheet
// line name the asset rolls onto (e.g. "Computer Equipment"), which the import slugifies
// + validates against the tenant's real BS asset lines. Read BY HEADER NAME (order-
// tolerant), so a column insertion no longer silently zeros the parse (the last position-
// based parser — the failure mode that returned EMPTY on the wide prepaid template).
/* THE HEADER ALIASES THIS PARSER READS -- the spec's own arrays (templateSpecs.js, the asset template), by
 * reference; the spec gate asserts it. They used to be a hand-written HEADER_ALIASES map here. Matched through
 * the ONE header rule (templateHeaders.normalizeHeader) -- W-062 pass 2. */
export const ASSET_HEADER_COLUMNS = {
    'asset.description': specColumnAliases('asset', 'description'),
    'asset.type': specColumnAliases('asset', 'type'),
    'asset.cost': specColumnAliases('asset', 'cost'),
    'asset.in_service_date': specColumnAliases('asset', 'in_service_date'),
    'asset.disposal_date': specColumnAliases('asset', 'disposal_date'),
    'asset.useful_life': specColumnAliases('asset', 'useful_life'),
    'asset.bs_line': specColumnAliases('asset', 'bs_line'),
    'asset.salvage_value': specColumnAliases('asset', 'salvage_value'),
    'asset.method': specColumnAliases('asset', 'method'),
};
// field -> the aliases, in the order the old map listed them
const HEADER_FIELDS = [['description', 'asset.description'], ['type', 'asset.type'], ['cost', 'asset.cost'],
    ['in_service_date', 'asset.in_service_date'], ['disposal_date', 'asset.disposal_date'], ['useful_life', 'asset.useful_life'],
    ['bs_line', 'asset.bs_line'], ['salvage', 'asset.salvage_value'], ['method', 'asset.method']].map(([f, k]) => [f, aliasSet(ASSET_HEADER_COLUMNS[k])]);
const _norm = h => String(h == null ? '' : h).trim().toLowerCase();

// Resolve the canonical column indices from a header row. Returns a { field: colIdx } map,
// or null when the row is not a canonical header (missing the two distinctive markers).
function resolveCanonicalColumns(row) {
    const cells = (row || []).map(normalizeHeader);
    const cols = {};
    for (const [field, aliases] of HEADER_FIELDS) {
        const idx = cells.findIndex(c => aliases.has(c));
        if (idx >= 0) cols[field] = idx;
    }
    // Distinctive markers: a real header row names BOTH description and bs_line.
    return (cols.description != null && cols.bs_line != null) ? cols : null;
}

// FA/IA (the `type` column) → the section a row belongs to. Fail-loud on an unknown token
// (the whole reason the canonical template carries an explicit column: a name alone is
// ambiguous — "Software" is both a fixed and an intangible asset).
function normalizeSection(t) {
    const s = _norm(t);
    if (['fa', 'f', 'fixed', 'fixed asset', 'fixed_asset', 'depreciation'].includes(s)) return 'fixed';
    if (['ia', 'i', 'intangible', 'intangible asset', 'intangible_asset', 'amortization'].includes(s)) return 'intangible';
    return null;
}

/**
 * Parse an asset workbook.
 *
 * One layout (W-063):
 *   • CANONICAL (preferred) — a header row naming `description` + `bs_line` (AST-1).
 *     Read by header name; the FA/IA `type` column drives the section; the friendly
 *     `bs_line` is slugified (via opts.normalizeKey) and validated (AST-2) against the
 *     tenant's real BS asset-line keys (opts.faKeys / opts.iaKeys). A bs_line that is not
 *     one of the tenant's <role> BS lines FAILS LOUD — a named error flag, never a guess
 *     and never a silent drop.
 *   - ANY OTHER SHEET (no header naming description + bs_line -- e.g. an old position-based D&A
 *     schedule): NOT read. Returned as { canonical: false } with no assets; the upload check's B7
 *     blocks it, naming the template's columns (W-063 -- the old positional reader is retired).
 *
 * @param {ArrayBuffer|Uint8Array} fileData
 * @param {object} [opts]
 * @param {string[]} [opts.faKeys]  valid fixed_asset BS line keys for this tenant
 * @param {string[]} [opts.iaKeys]  valid intangible_asset BS line keys for this tenant
 * @param {(s:string)=>string} [opts.normalizeKey]  the BS slugifier (AdminPage normalizeBSKey)
 *        — passed in so there is ONE slugifier, not a copy in this util.
 * @returns {{ fixed_assets, intangible_assets, flags, sheetName, canonical:boolean }}
 */
export default function parseAssetExcel(fileData, opts = {}) {
    const wb = XLSX.read(fileData, { type: 'array', cellDates: false });
    // Pick D&A Sch sheet if present; else the first sheet (canonical sheets are usually first).
    let sheetName = wb.SheetNames.find(n => /d.?a\s*sch/i.test(n)) || wb.SheetNames[0];
    const sheet = wb.Sheets[sheetName];
    if (!sheet) {
        return { fixed_assets: [], intangible_assets: [], flags: [{ type: 'error', row: 0, message: 'No sheet found in workbook.' }], sheetName: null, canonical: false };
    }
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null, raw: true });

    // Locate a canonical header row in the first several rows.
    let headerIdx = -1, cols = null;
    for (let i = 0; i < Math.min(rows.length, 10); i++) {
        const c = resolveCanonicalColumns(rows[i]);
        if (c) { headerIdx = i; cols = c; break; }
    }
    if (cols) return parseCanonical(rows, headerIdx, cols, sheetName, opts);
    // No bs_line header: nothing is read (W-063). B7 names it at upload.
    return { fixed_assets: [], intangible_assets: [], flags: [], sheetName, canonical: false };
}

// ── Canonical (header-named) parse ───────────────────────────
function parseCanonical(rows, headerIdx, cols, sheetName, opts) {
    const { normalizeKey } = opts;
    const validate = Array.isArray(opts.faKeys) || Array.isArray(opts.iaKeys);
    const faSet = new Set(opts.faKeys || []);
    const iaSet = new Set(opts.iaKeys || []);

    const fixed_assets = [];
    const intangible_assets = [];
    const flags = [];

    for (let i = headerIdx + 1; i < rows.length; i++) {
        const row = rows[i] || [];
        const rowNum = i + 1;
        const name = String(row[cols.description] == null ? '' : row[cols.description]).trim();
        if (!name) continue; // blank row

        const section = normalizeSection(cols.type != null ? row[cols.type] : '');
        if (!section) {
            // `column` (W-068): which spec column the error is about, so Step 8 names one fault once beside the template check.
            flags.push({ type: 'error', column: 'type', row: rowNum, message: `Row ${rowNum}: "${name}" has an unrecognized type "${cols.type != null ? row[cols.type] : ''}" \u2014 use FA (fixed) or IA (intangible).` });
            continue;
        }

        const cost = parseNumber(row[cols.cost]);
        const purchase_date = parseExcelDate(cols.in_service_date != null ? row[cols.in_service_date] : null);
        const disposal_date = parseExcelDate(cols.disposal_date != null ? row[cols.disposal_date] : null); // blank → null
        const useful_life_years = parseIntSafe(cols.useful_life != null ? row[cols.useful_life] : null, 0);
        const bs_line = String(row[cols.bs_line] == null ? '' : row[cols.bs_line]).trim();
        // Optional column: absent from the sheet, or blank in the cell, both yield 0 via
        // parseNumber. NOT flagged -- a $0 salvage is the normal case, and a flag that
        // fires on nearly every row is a flag nobody reads.
        const salvage = parseNumber(cols.salvage != null ? row[cols.salvage] : null);
        // W-063: the method, through its owner. Blank -> SL. An unknown value keeps SL here and is BLOCKED by the upload
        // check (B3, derived from the spec's values), so it can never be imported as SL by accident.
        const method = normalizeDepMethod(cols.method != null ? row[cols.method] : null) || DEFAULT_DEP_METHOD;

        if (!purchase_date) flags.push({ type: 'missing_date', row: rowNum, message: `Row ${rowNum}: "${name}" missing in-service date — will need manual entry before import.` });
        if (!(cost > 0)) flags.push({ type: 'zero_cost', row: rowNum, message: `Row ${rowNum}: "${name}" has zero or missing cost.` });
        if (!(useful_life_years > 0)) flags.push({ type: 'missing_life', row: rowNum, message: `Row ${rowNum}: "${name}" missing useful life — defaulted to 5 years.` });

        // AST-2: resolve + validate the friendly bs_line against the tenant's real BS keys.
        // slug = normalizeKey(bs_line); bs_line_id = slug; NEVER inferred from the asset name.
        let asset_type = ''; // downstream field that carries the chosen bs_line_id
        if (!bs_line) {
            flags.push({ type: 'error', column: 'bs_line', row: rowNum, message: `Row ${rowNum}: "${name}" has no Balance Sheet line \u2014 set the bs_line column to one of the tenant's ${section === 'fixed' ? 'fixed' : 'intangible'}-asset lines.` });
        } else if (typeof normalizeKey === 'function') {
            const slug = normalizeKey(bs_line);
            const validKeys = section === 'fixed' ? faSet : iaSet;
            if (validate && !validKeys.has(slug)) {
                const roleWord = section === 'fixed' ? 'fixed-asset' : 'intangible-asset';
                const opts_ = [...validKeys];
                flags.push({ type: 'error', row: rowNum, message: `Row ${rowNum}: "${name}" Balance Sheet line "${bs_line}" (→ ${slug || '∅'}) is not a ${roleWord} line for this tenant.${opts_.length ? ` Valid: ${opts_.join(', ')}.` : ''}` });
            } else {
                asset_type = slug; // resolved (validated when keys were supplied)
            }
        } else {
            // No slugifier supplied (a bare parser call): keep the raw friendly value so a caller
            // can slug/validate itself; do not fabricate a key.
            asset_type = '';
        }

        const record = {
            name,
            asset_type,
            bs_line, // raw friendly value, retained for messages / display
            cost: r2(cost),
            purchase_date: purchase_date || '',
            disposal_date: disposal_date || '',
            useful_life_years: useful_life_years > 0 ? useful_life_years : 5,
            method,
            // Carried on BOTH sections so the record shape is uniform, but it is
            // MEANINGFUL ONLY FOR FIXED ASSETS: intangible_assets has no salvage_value
            // column and its INSERT never names one, so an IA row's value is inert by
            // construction rather than by a filter someone has to remember to keep.
            salvage_value: r2(salvage),
        };
        if (section === 'fixed') fixed_assets.push(record);
        else intangible_assets.push(record);
    }

    /* W-068 -- what the template check reads: this parser's header->column map under the SPEC's column names and the
     * raw rows (row i is spreadsheet row i + 1). A non-template sheet carries none (the check's B7 reads `canonical`). */
    const specScan = { template: 'asset', layout: 'template', headerRow: headerIdx, monthCols: {}, rows,
        cols: { description: cols.description ?? null, type: cols.type ?? null, cost: cols.cost ?? null, in_service_date: cols.in_service_date ?? null,
            disposal_date: cols.disposal_date ?? null, useful_life: cols.useful_life ?? null, bs_line: cols.bs_line ?? null, salvage_value: cols.salvage ?? null, method: cols.method ?? null } };
    return { fixed_assets, intangible_assets, flags, sheetName, canonical: true, specScan };
}

export { parseExcelDate, normalizeSection, resolveCanonicalColumns };

// The blank asset template now lives in templateGenerators.js (generateAssetBlank /
// downloadAssetBlank), built from the shared TEMPLATE_SPECS so it can never drift from this
// parser. The old legacy-D&A-layout generateAssetTemplate/downloadAssetTemplate were removed
// with the Step-4 blanks pass — a second asset-template code path was the drift this fixes.
